/**
 * Invoice Dialog Component
 * Displays invoice details for out-of-pocket payment sessions
 */

import React, { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Divider,
  TextField,
  IconButton,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material'
import { Close as CloseIcon, Edit as EditIcon } from '@mui/icons-material'
import { format } from 'date-fns'
import pillarTheraLogo from '@/assets/pillarThera.png'

interface InvoiceData {
  _id: string
  sessionId?: string
  invoice_ids: string
  contract_ids: string
  session_ids: string
  patient_name: string
  patientId: {
    firstName: string
    lastName: string
    addressLine1: string
    addressLine2?: string
    city: string
    state: string
    postalCode: string
    country: string
    email: string
  }
  therapistId: {
    firstName: string
    lastName: string
    billingAddressLine: string
    billingCity: string
    billingState: string
    billingZipCode: string
    billingPhone: string
  }
  bill_from: string
  bill_to: string
  bill_address?: string
  bill_city?: string
  bill_state?: string
  bill_zip?: string
  service_name: string
  totalAmount?: number
  amount: number
  discount: number
  issueDate: string
  dueDate: string
  payment_date: string
  payment_method: string
  status: string
}

interface InvoiceDialogProps {
  open: boolean
  onClose: () => void
  invoiceData: InvoiceData | null
  loading?: boolean
  readOnly?: boolean
  onGenerateInvoice?: (data: {
    session_id: string
    bill_address: string
    bill_city: string
    bill_state: string
    bill_zip: string
    discount: number
    amount: number
    // service_name: string
  }) => void
}

const InvoiceDialog: React.FC<InvoiceDialogProps> = ({
  open,
  onClose,
  invoiceData,
  loading = false,
  readOnly = false,
  onGenerateInvoice,
}) => {
  const [editingDiscount, setEditingDiscount] = useState(false)
  const [editingBillTo, setEditingBillTo] = useState(false)
  const [editingServiceName, setEditingServiceName] = useState(false)

  const [discountValue, setDiscountValue] = useState(0)
  const [amountValue, setAmountValue] = useState(0)
  const [billAddress, setBillAddress] = useState('')
  const [billCity, setBillCity] = useState('')
  const [billState, setBillState] = useState('')
  const [billZip, setBillZip] = useState('')
  const [serviceNameValue, setServiceNameValue] = useState('')
  const [generating, setGenerating] = useState(false)

  // Helper function to parse bill_to string into individual components
  const parseBillToAddress = (billTo: string, data: InvoiceData | null) => {
    // If we have individual fields, use them directly (highest priority)
    if (data?.bill_address || data?.bill_city || data?.bill_state || data?.bill_zip) {
      return {
        address: data.bill_address || '',
        city: data.bill_city || '',
        state: data.bill_state || '',
        zip: data.bill_zip || '',
      }
    }

    // Otherwise, try to parse from bill_to string
    if (!billTo) return { address: '', city: '', state: '', zip: '' }

    // Try to parse the format: "17853 Pierce Plaza Omaha Nebraska 68130 US"
    const parts = billTo.trim().split(/\s+/)

    // Common format: "Address City State Zip Country"
    // We'll try to extract: last 2 parts might be country/zip, second to last might be zip, etc.
    if (parts.length >= 4) {
      // Assume format: Address (multiple words) City State Zip [Country]
      // Try to find zip (usually numeric, 5 digits)
      let zipIndex = -1
      for (let i = parts.length - 1; i >= 0; i--) {
        if (/^\d{5}(-\d{4})?$/.test(parts[i])) {
          zipIndex = i
          break
        }
      }

      if (zipIndex > 0) {
        const zip = parts[zipIndex]
        const state = parts[zipIndex - 1] || ''
        const city = parts[zipIndex - 2] || ''
        const address = parts.slice(0, zipIndex - 2).join(' ') || ''

        return { address, city, state, zip }
      }
    }

    // Fallback: return as-is in address field
    return { address: billTo, city: '', state: '', zip: '' }
  }

  React.useEffect(() => {
    if (invoiceData) {
      setDiscountValue(invoiceData.discount || 0)
      // Use totalAmount as the base amount (subtotal), fallback to amount if totalAmount not available
      // If totalAmount exists, use it; otherwise calculate from amount + discount
      const baseAmount =
        invoiceData.totalAmount ??
        invoiceData.amount + (invoiceData.discount || 0)
      setAmountValue(baseAmount)
      setServiceNameValue(invoiceData.service_name || '')

      // Parse bill_to address into individual components
      const parsedAddress = parseBillToAddress(invoiceData?.bill_to || '', invoiceData)
      setBillAddress(parsedAddress.address)
      setBillCity(parsedAddress.city)
      setBillState(parsedAddress.state)
      setBillZip(parsedAddress.zip)
    }
  }, [invoiceData])

  if (!invoiceData && !loading) {
    return null
  }

  // Use totalAmount as subtotal if available, otherwise use amountValue
  const subtotal = invoiceData?.totalAmount ?? amountValue
  const discount = discountValue
  // Calculate amount due as subtotal - discount (always recalculate to reflect discount changes)
  const amountDue = subtotal - discount

  const handleDiscountSave = () => {
    setEditingDiscount(false)
  }

  const handleBillToSave = () => {
    setEditingBillTo(false)
  }

  const handleServiceNameSave = () => {
    setEditingServiceName(false)
  }

  // Helper function to format address for display
  const formatAddressForDisplay = () => {
    const parts = [billAddress, billCity, billState, billZip].filter(Boolean)
    return parts.length > 0 ? parts.join(' ') : ''
  }

  const handleGenerateInvoice = async () => {
    if (invoiceData) {
      setGenerating(true)

      // Calculate subtotal for payload (use totalAmount if available, otherwise use amountValue)
      const subtotalForPayload = invoiceData?.totalAmount ?? amountValue

      const payload = {
        session_id: invoiceData.sessionId || invoiceData._id,
        bill_address: billAddress,
        bill_city: billCity,
        bill_state: billState,
        bill_zip: billZip,
        discount: discountValue,
        amount: subtotalForPayload, // Send the base amount (totalAmount) before discount
        // service_name: serviceNameValue,
      }

      await onGenerateInvoice?.(payload)
      setGenerating(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: '600px',
        },
      }}
    >
      <DialogContent sx={{ p: 4, position: 'relative' }}>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="500px"
          >
            <CircularProgress size={60} />
          </Box>
        ) : (
          <>
            {/* Header */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="start"
              mb={4}
            >
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  color: '#6B7280',
                  fontSize: '2rem',
                }}
              >
                Invoice
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <img
                  src={pillarTheraLogo}
                  alt="PillarThera"
                  style={{ height: '50px' }}
                />
                <IconButton
                  onClick={onClose}
                  size="small"
                  sx={{
                    color: 'grey.500',
                    '&:hover': { color: 'grey.700' },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>

            {/* Invoice Details */}
            <Box mb={3}>
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, color: '#6B7280', mb: 0.5 }}
              >
                Invoice Number: {invoiceData?.invoice_ids || '0001'}
              </Typography>
              <Box mt={2}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: '#6B7280' }}
                >
                  Date of issue:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {invoiceData?.issueDate
                    ? format(new Date(invoiceData.issueDate), 'MM-dd-yyyy')
                    : format(new Date(), 'MM-dd-yyyy')}
                </Typography>
              </Box>
              <Box mt={1}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: '#6B7280' }}
                >
                  Date due:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {invoiceData?.dueDate
                    ? format(new Date(invoiceData.dueDate), 'MM-dd-yyyy')
                    : format(new Date(), 'MM-dd-yyyy')}
                </Typography>
              </Box>
            </Box>

            {/* From and Bill To Section */}
            <Box display="flex" justifyContent="space-between" mb={4} gap={4}>
              {/* From */}
              <Box flex={1}>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 600, color: '#6B7280', mb: 1 }}
                >
                  From
                </Typography>
                {/* <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  PillarThera Rx IIC
                </Typography> */}
                <Typography variant="body2" color="text.secondary">
                  {invoiceData?.bill_from || 'PillarThera Rx IIC'}
                </Typography>
                {/* <Typography variant="body2" color="text.secondary">
                  Ste 100 @120213
                </Typography> */}
                {/* <Typography variant="body2" color="text.secondary">
                  {invoiceData?.therapistId.billingCity || 'Dallas'},{' '}
                  {invoiceData?.therapistId.billingState || 'Texas'}{' '}
                  {invoiceData?.therapistId.billingZipCode || '75231'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {invoiceData?.therapistId.billingPhone || '+1 123-456-789'}
                </Typography> */}
              </Box>

              {/* Bill To */}
              <Box flex={1}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, color: '#6B7280' }}
                  >
                    Bill to
                  </Typography>
                  {!readOnly && (
                    <IconButton
                      size="small"
                      sx={{ color: '#6B7280' }}
                      onClick={() => setEditingBillTo(!editingBillTo)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  {invoiceData?.patient_name ||
                    (invoiceData?.patientId &&
                      typeof invoiceData.patientId === 'object' &&
                      invoiceData.patientId.firstName
                      ? `${invoiceData.patientId.firstName} ${invoiceData.patientId.lastName || ''}`.trim()
                      : 'N/A')}
                </Typography>
                {editingBillTo && !readOnly ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 1 }}>
                    <TextField
                      fullWidth
                      label="Address"
                      size="small"
                      value={billAddress}
                      onChange={e => setBillAddress(e.target.value)}
                      autoFocus
                    />
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <TextField
                        fullWidth
                        label="City"
                        size="small"
                        value={billCity}
                        onChange={e => setBillCity(e.target.value)}
                      />
                      <TextField
                        fullWidth
                        label="State"
                        size="small"
                        value={billState}
                        onChange={e => setBillState(e.target.value)}
                      />
                      <TextField
                        fullWidth
                        label="Zip"
                        size="small"
                        value={billZip}
                        onChange={e => setBillZip(e.target.value)}
                      />
                    </Box>
                  </Box>
                ) : (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      {formatAddressForDisplay() || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {invoiceData?.patientId?.email || ''}
                    </Typography>
                  </>
                )}
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Invoice Table */}
            <TableContainer component={Paper} elevation={0} sx={{ mb: 3 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: '#6B7280' }}>
                      Description
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontWeight: 600, color: '#6B7280' }}
                    >
                      Qty
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontWeight: 600, color: '#6B7280' }}
                    >
                      Unit price
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 600, color: '#6B7280' }}
                    >
                      Amount
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {/* {editingServiceName ? (
                          <TextField
                            size="small"
                            value={serviceNameValue}
                            onChange={e => setServiceNameValue(e.target.value)}
                            onBlur={handleServiceNameSave}
                            autoFocus
                            fullWidth
                          />
                        ) : ( */}
                        <Typography variant="body2">
                          {serviceNameValue}
                        </Typography>
                        {/* )} */}
                        {/* <IconButton
                          size="small"
                          sx={{ color: '#6B7280' }}
                          onClick={() =>
                            setEditingServiceName(!editingServiceName)
                          }
                        >
                          <EditIcon fontSize="small" />
                        </IconButton> */}
                      </Box>
                    </TableCell>
                    <TableCell align="center">1</TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        ${subtotal.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        ${subtotal.toFixed(2)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {/* Totals */}
            <Box display="flex" justifyContent="flex-end" mb={3}>
              <Box minWidth="300px">
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography variant="body2" color="text.secondary">
                    Subtotal
                  </Typography>
                  <Typography variant="body2">
                    ${subtotal.toFixed(2)}
                  </Typography>
                </Box>

                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography variant="body2" color="text.secondary">
                    Discount
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    {editingDiscount && !readOnly ? (
                      <TextField
                        size="small"
                        type="number"
                        value={discountValue}
                        onChange={e =>
                          setDiscountValue(parseFloat(e.target.value) || 0)
                        }
                        onBlur={handleDiscountSave}
                        sx={{ width: '80px' }}
                        InputProps={{
                          startAdornment: <Typography>$</Typography>,
                        }}
                      />
                    ) : (
                      <>
                        <Typography variant="body2">
                          ${discount.toFixed(2)}
                        </Typography>
                        {!readOnly && (
                          <IconButton
                            size="small"
                            onClick={() => setEditingDiscount(true)}
                            sx={{ color: '#6B7280' }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                      </>
                    )}
                  </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Amount due
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    ${amountDue.toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 4, pb: 3, gap: 2 }}>
        {readOnly ? (
          <Button
            onClick={onClose}
            variant="contained"
            size="large"
            sx={{
              textTransform: 'none',
              px: 4,
              py: 1.5,
              borderRadius: 2,
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            }}
          >
            Close
          </Button>
        ) : (
          <>
            <Button
              onClick={onClose}
              variant="outlined"
              size="large"
              sx={{
                textTransform: 'none',
                px: 4,
                py: 1.5,
                borderRadius: 2,
                borderColor: '#D1D5DB',
                color: '#6B7280',
                '&:hover': {
                  borderColor: '#9CA3AF',
                  backgroundColor: '#F9FAFB',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleGenerateInvoice}
              variant="contained"
              size="large"
              disabled={loading || generating}
              sx={{
                textTransform: 'none',
                px: 4,
                py: 1.5,
                borderRadius: 2,
                backgroundColor: '#2E7DAF',
                '&:hover': {
                  backgroundColor: '#2563a0',
                },
              }}
            >
              {generating ? (
                <>
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                  Generating...
                </>
              ) : (
                'Generate Invoice'
              )}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default InvoiceDialog
